import * as Sentry from '@sentry/react';
import React, { useEffect, useRef, useState } from 'react';

type StorageType = 'local' | 'session';

function getStorage(storageType: StorageType): Storage | null {
  try {
    return storageType === 'local' ? localStorage : sessionStorage;
  } catch {
    return null;
  }
}

function initState<T>(defaultValue: T, stateKey: string, storageType: StorageType) {
  try {
    const localState = getStorage('local')?.getItem(stateKey);
    const sessionState = getStorage('session')?.getItem(stateKey);

    if (storageType === 'local' && localState) {
      return JSON.parse(localState) as T;
    }

    if (storageType === 'session' && sessionState) {
      return JSON.parse(sessionState) as T;
    }
  } catch {
    return defaultValue;
  }

  return defaultValue;
}

const useStorageState = <T>(
  stateKey: string,
  defaultValue: T,
  storageType: StorageType = 'local',
) => {
  const [state, setState] = useState<T>(() => initState(defaultValue, stateKey, storageType));
  const isNewSession = useRef(true);

  const storage = getStorage(storageType);

  useEffect(() => {
    if (isNewSession.current) {
      try {
        const storageState = storage?.getItem(stateKey);

        if (storageState) {
          setState(JSON.parse(storageState) as T);
        } else {
          setState(defaultValue);
        }
      } catch {
        setState(defaultValue);
      }

      isNewSession.current = false;
      return;
    }

    try {
      storage?.setItem(stateKey, JSON.stringify(state));
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTags({
          message: `Error trying to set sessionStorage ${stateKey}, ${JSON.stringify(state)}`,
          file: '?',
          fn: 'useStorageState()-01',
        });
        Sentry.captureException(error);
      });
    }
  }, [state, stateKey, defaultValue, storage]);

  const updateState = React.useCallback(
    (newState: T) => {
      setState(newState);
      storage?.setItem(stateKey, JSON.stringify(newState));
    },
    [stateKey, storage],
  );

  const clearStorage = React.useCallback(() => {
    storage?.removeItem(stateKey);
  }, [stateKey, storage]);

  return [state, updateState, clearStorage] as const;
};

export default useStorageState;
